import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';

import { InterruptionModeSelect } from '@/components/atoms/ToolSettingsSelect/ToolSettingsSelect';
import { Button } from '@/components/ui/button';
import { URLInput } from '@/components/ui/custom/URLInput/URLInput';
import {
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input as ShadcnInput } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { DEFAULT_TOOL_SETTINGS } from '@/shared/types/toolSettings';

import { WebhookHeadersFieldArray } from './WebhookHeadersFieldArray';
import { WebhookFormCommon } from './webhookTypes';

const generateToken = (length = 16): string => {
  const chars = 'abcdefghijklmnopqrstuvwxyz0123456789';
  const array = new Uint8Array(length);
  crypto.getRandomValues(array);
  return Array.from(array, (byte) => chars[byte % chars.length]).join('');
};

interface WebhookSetupTabContentProps {
  autoFocusUrl?: boolean;
  showToolSettings?: boolean;
  canWrite?: boolean;
}

export const WebhookSetupTabContent = ({
  autoFocusUrl = false,
  showToolSettings = false,
  canWrite = true,
}: WebhookSetupTabContentProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<WebhookFormCommon>();

  const handleGenerateToken = () => {
    const token = generateToken(16);
    setValue('token', token, { shouldValidate: true, shouldDirty: true });
    toast.success(t('webhook.config.token_generated'));
  };

  return (
    <div className="mx-1 flex h-full flex-col">
      <FormField
        control={control}
        name="name"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">{t('webhook.config.name')}</FormLabel>
            <FormControl>
              <ShadcnInput {...field} />
            </FormControl>
            <div className="-mt-2 min-h-5">
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="url"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">{t('webhook.config.url')}</FormLabel>
            <FormControl>
              <URLInput
                value={field.value ?? ''}
                onChange={field.onChange}
                autoFocus={autoFocusUrl}
              />
            </FormControl>
            <div className="-mt-2 min-h-5">
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="token"
        render={({ field }) => (
          <FormItem>
            <FormLabel className="text-foreground">{t('webhook.config.token')}</FormLabel>
            <div className="flex gap-2">
              <FormControl>
                <ShadcnInput
                  {...field}
                  value={field.value ?? ''}
                  placeholder={t('webhook.config.token_placeholder')}
                />
              </FormControl>
              <Button
                type="button"
                variant="outline-neutral"
                onClick={handleGenerateToken}
                className="shrink-0"
              >
                {t('webhook.config.generate_token')}
              </Button>
            </div>
            <div className="-mt-2 min-h-5">
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
      <FormField
        control={control}
        name="includeHeaders"
        render={({ field }) => (
          <FormItem>
            <div className="rounded-lg border border-border bg-background px-4 py-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="space-y-1">
                  <FormLabel className="text-base font-semibold text-foreground">
                    {t('webhook.config.headers_title')}
                  </FormLabel>
                  <FormDescription>{t('webhook.config.headers_subtitle')}</FormDescription>
                </div>
                <FormControl>
                  <Switch
                    checked={field.value}
                    onCheckedChange={(checked) => field.onChange(checked === true)}
                    aria-label={t('webhook.config.headers_title')}
                  />
                </FormControl>
              </div>
            </div>
            <div className="-mt-2 min-h-5">
              <FormMessage />
            </div>
          </FormItem>
        )}
      />
      <WebhookHeadersFieldArray />
      {showToolSettings && (
        <FormField
          control={control}
          name="interruption_mode"
          render={({ field }) => (
            <FormItem>
              <div className="space-y-1">
                <FormLabel className="text-base font-semibold text-foreground">
                  {t('voice_tool_settings.interruption_mode')}
                </FormLabel>
                <FormDescription>
                  {t('webhook.config.interruption_mode_description')}
                </FormDescription>
              </div>
              <FormControl>
                <InterruptionModeSelect
                  aria-label={t('voice_tool_settings.interruption_mode')}
                  value={field.value ?? DEFAULT_TOOL_SETTINGS.interruption_mode}
                  disabled={!canWrite}
                  onChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
      )}
    </div>
  );
};
